import 'components/ordering/multiple-baskets/basket-form/basket-form.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { html, LitElement, TemplateResult } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { BASKET_API, BasketApi } from 'components/ordering/multiple-baskets/basket-api/basket-api.js'
import { NameBasketRequest } from 'components/ordering/multiple-baskets/basket-api/name-basket.js'
import { User } from 'components/ordering/multiple-baskets/basket-api/user.js'
import {
  BasketChangedEvent,
  BasketFormFinishedEvent,
  BasketNameChosenEvent,
  BasketNameValidationErrorEvent,
  SelectDeliveryPolicyEvent,
} from 'components/ordering/multiple-baskets/basket-events.js'
import { BasketListItem } from 'components/ordering/multiple-baskets/basket-store/basket-list-parser.js'
import {
  BASKET_LIST_MUTATOR,
  BASKET_LIST_STORE,
  BasketListMutator,
  BasketListStore,
} from 'components/ordering/multiple-baskets/basket-store/basket-list-store.js'
import { SetBasketDeliveryPolicyRequest } from 'components/ordering/multiple-baskets/basket-api/delivery-policy.js'
import { NameBasketFailure, NameBasketUnionResponse } from 'components/ordering/acl/internal-types.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { WINDOW_LOCATION, WindowLocation } from '@cloudsuite/mosaic-shared/scripts/window-location.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'


@customElement('csc-basket-editor')
export class CSCBasketEditor extends LitElement {
  @property({ attribute: 'ordering-endpoint' })
  orderingEndpoint = ''

  @property({ type: Number, attribute: 'shop-id' })
  shopId: number | null = null

  @property({ type: Number, attribute: 'party-id' })
  partyId: number | null = null

  @property({ type: Number, attribute: 'user-id' })
  userId: number | null = null

  private get user(): User {
    if (this.userId !== null && this.partyId !== null && this.shopId !== null) {
      return { userId: this.userId, shopId: this.shopId, partyId: this.partyId }
    }
    throw new Error('user-id, party-id and shop-id are mandatory.')
  }

  @state()
  private _selectedBasket: BasketListItem | null = null

  private get selectedBasket(): BasketListItem {
    if (this._selectedBasket !== null) {
      return this._selectedBasket
    }
    throw new Error('no selected basket')
  }

  @inject(BASKET_LIST_MUTATOR)
  private basketListMutator!: BasketListMutator

  @inject(BASKET_LIST_STORE)
  private basketListStore!: BasketListStore

  @inject(BASKET_API)
  private basketApi!: BasketApi

  @inject(WINDOW_LOCATION)
  private windowLocation!: WindowLocation

  constructor() {
    super()
    this.addEventListener(BasketFormFinishedEvent.type, this.handleSave)
  }

  private async handleSave(e: BasketFormFinishedEvent) {
    const nameBasketResponse= await this.setBasketName(e)
    if (nameBasketResponse instanceof NameBasketFailure){
      window.dispatchEvent(new BasketNameValidationErrorEvent(nameBasketResponse.failureReason))
      return
    }
    await this.setDeliveryPolicy(e)
    this.finishEdit()
  }

  private setBasketName = async (event: BasketNameChosenEvent): Promise<NameBasketUnionResponse> => {
    const request = new NameBasketRequest(
      this.orderingEndpoint,
      this.selectedBasket.basketId,
      event.name
    )

    return await this.basketApi.nameBasket(request)
  }

  private setDeliveryPolicy = async (event: SelectDeliveryPolicyEvent) => {
    if (this.shopId === null) {
      throw new Error('shop-id is mandatory.')
    }

    const setBasketDeliveryPolicyRequest = new SetBasketDeliveryPolicyRequest(
      this.orderingEndpoint,
      this.shopId,
      this.selectedBasket.basketId,
      event.selectedDeliveryPolicy,
    )

    await this.basketApi.setBasketDeliveryPolicy(setBasketDeliveryPolicyRequest)
  }

  private finishEdit = () => {
    this.dispatchEvent(new BasketChangedEvent())
    this.windowLocation.reload()
  }

  protected override render(): TemplateResult {
    const deliveryPolicy = this._selectedBasket?.deliveryPolicy
    return html`
      <csc-basket-form
        ordering-endpoint="${this.orderingEndpoint}"
        party-id="${this.partyId}"
        shop-id="${this.shopId}"
        basket-name="${this._selectedBasket?.name}"
        basket-delivery-policy="${JSON.stringify(deliveryPolicy)}">
        <div slot="submit-text">
          ${t('save_basket_button', { ns: 'csc-basket-editor', default: 'Yes, save' })}
        </div>
      </csc-basket-form>
    `
  }

  override connectedCallback() {
    super.connectedCallback()

    createEffect(() => {
      this._selectedBasket = this.basketListStore.selectedBasket()
    })

    this.basketListMutator.initialize(this.user)
  }
}
